//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ProductMetaFieldsViewOnly } from '@campaigns/components/shared/product-details-modal/details/ProductMetaFieldsViewOnly';
import { BrandSearchField } from '@campaigns/components/shared/product-details-modal/form/BrandSearchField';
import { CategorySearchField } from '@campaigns/components/shared/product-details-modal/form/CategorySearchField';
import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { ProductDetailsFieldLabels } from '@campaigns/constants/ProductDetailsModalFieldLabels';
import { URL_REGEX_RULE } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';

interface ProductMetaFieldsProps {
  isViewOnly: boolean;
}

export const ProductMetaFields = ({ isViewOnly }: ProductMetaFieldsProps) => {
  const { control, getValues } = useFormContext<EditableBVSamplingProduct>();

  if (isViewOnly) {
    const brandValue = getValues('brand');
    const categoryValue = getValues('category');
    const pdpUrlValue = getValues('pdpUrl');

    return (
      <ProductMetaFieldsViewOnly
        brand={brandValue}
        category={categoryValue}
        pdpUrl={pdpUrlValue}
      />
    );
  }

  return (
    <Container>
      {/* Product Brand Field with Typeahead Search */}
      <FormFieldController
        control={control}
        name='brand'
        rules={{
          required: 'Please enter a product brand',
        }}
      >
        {({ error, onChange, value }) => (
          <BrandSearchField
            error={error}
            onSelectBrand={onChange}
            value={value}
          />
        )}
      </FormFieldController>

      {/* Product Category Field with Typeahead Search */}
      <FormFieldController
        control={control}
        name='category'
        rules={{
          required: 'Please enter a product category',
        }}
      >
        {({ error, onChange, value }) => (
          <CategorySearchField
            error={error}
            onSelectCategory={onChange}
            value={value}
          />
        )}
      </FormFieldController>

      {/* Product URL Field (optional) */}
      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.pdpUrl}
        labelHelpText=''
        name='pdpUrl'
        placeholder='https://'
        rules={{
          pattern: {
            message: 'Please enter a valid URL',
            value: URL_REGEX_RULE,
          },
        }}
        visibleToParticipants={true}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
